import type { Tree } from "../data/types";

export const SITE_ORIGIN = "https://trees.cy";
export const SITE_NAME = "Ancient Trees of Cyprus";
export const TEAM_NAME = "Cyprus Trees team";
export const DEFAULT_SOCIAL_IMAGE = `${SITE_ORIGIN}/social/ancient-trees-cyprus.png`;
export const LOGO_IMAGE = `${SITE_ORIGIN}/icons/icon-512.png`;

export const SEO_LANGUAGES = ["en", "ru", "el"] as const;
export type SeoLanguage = (typeof SEO_LANGUAGES)[number];

const OPEN_GRAPH_LOCALES: Record<SeoLanguage, string> = {
  en: "en_CY",
  ru: "ru_RU",
  el: "el_GR",
};

export interface SeoImage {
  url: string;
  width?: number;
  height?: number;
  type?: string;
}

export interface CatalogueItem {
  name: string;
  path: string;
}

export interface DocumentSeo {
  title: string;
  description: string;
  pathname: string;
  language: SeoLanguage;
  image: SeoImage;
  imageAlt: string;
  type?: "website" | "article";
  noindex?: boolean;
  structuredData: Record<string, unknown>;
}

export function seoLanguage(language: string): SeoLanguage {
  return SEO_LANGUAGES.includes(language as SeoLanguage) ? language as SeoLanguage : "en";
}

export function localizedUrl(pathname: string, language: SeoLanguage): string {
  const url = new URL(pathname, SITE_ORIGIN);
  url.hash = "";
  url.search = "";
  if (language !== "en") url.searchParams.set("lang", language);
  return url.toString();
}

export function truncateDescription(value: string, limit = 165): string {
  const compact = value.replace(/\s+/g, " ").trim();
  if (compact.length <= limit) return compact;
  const prefix = compact.slice(0, limit - 1);
  const wholeWords = compact[limit - 1] === " " ? prefix : prefix.replace(/\s+\S*$/, "");
  const shortened = wholeWords.replace(/[,:;\s]+$/, "");
  return `${shortened}…`;
}

export function treeSeoImage(tree: Tree): SeoImage {
  const photo = tree.photos[0];
  if (!photo) return { url: DEFAULT_SOCIAL_IMAGE, width: 1200, height: 630, type: "image/png" };
  if (photo.kind === "external") {
    return { url: new URL(photo.url, SITE_ORIGIN).href, width: photo.width, height: photo.height };
  }

  const width = Math.max(...photo.widths);
  return {
    url: `${SITE_ORIGIN}${photo.basePath}-${width}.jpeg`,
    width,
    height: Math.round(photo.height * (width / photo.width)),
    type: "image/jpeg",
  };
}

function organization() {
  return {
    "@type": "Organization",
    "@id": `${SITE_ORIGIN}/#organization`,
    name: TEAM_NAME,
    url: `${SITE_ORIGIN}/`,
    logo: {
      "@type": "ImageObject",
      url: LOGO_IMAGE,
      width: 512,
      height: 512,
    },
  };
}

function website(description: string) {
  return {
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}/#website`,
    url: `${SITE_ORIGIN}/`,
    name: SITE_NAME,
    description,
    inLanguage: [...SEO_LANGUAGES],
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  };
}

export function buildStructuredData({
  kind,
  url,
  title,
  description,
  language,
  image,
  catalogueItems = [],
  tree,
  treeName,
  placeName,
  siteDescription,
}: {
  kind: "home" | "catalogue" | "map" | "tree" | "notFound";
  url: string;
  title: string;
  description: string;
  language: SeoLanguage;
  image: SeoImage;
  catalogueItems?: CatalogueItem[];
  tree?: Tree;
  treeName?: string;
  placeName?: string;
  siteDescription?: string;
}): Record<string, unknown> {
  const graph: Record<string, unknown>[] = [organization(), website(siteDescription ?? description)];
  const pageId = `${url}#webpage`;
  const page: Record<string, unknown> = {
    "@type": kind === "catalogue" || kind === "map" ? "CollectionPage" : kind === "tree" ? "Article" : "WebPage",
    "@id": pageId,
    url,
    name: title,
    description,
    inLanguage: language,
    isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: image.url,
      ...(image.width ? { width: image.width } : {}),
      ...(image.height ? { height: image.height } : {}),
    },
  };

  if (kind === "home") page.about = { "@id": `${SITE_ORIGIN}/#organization` };
  if ((kind === "catalogue" || kind === "map") && catalogueItems.length > 0) {
    page.mainEntity = {
      "@type": "ItemList",
      numberOfItems: catalogueItems.length,
      itemListElement: catalogueItems.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        url: localizedUrl(item.path, language),
      })),
    };
  }
  if (kind === "tree" && tree && treeName) {
    page.headline = treeName;
    page.author = { "@id": `${SITE_ORIGIN}/#organization` };
    page.publisher = { "@id": `${SITE_ORIGIN}/#organization` };
    page.about = {
      "@type": "Thing",
      name: treeName,
      ...(tree.species?.binomial ? { alternateName: tree.species.binomial } : {}),
    };
    page.contentLocation = {
      "@type": "Place",
      name: placeName || treeName,
      ...(tree.location.precision !== "unknown" && tree.location.lat !== null && tree.location.lon !== null ? { geo: {
        "@type": "GeoCoordinates",
        latitude: tree.location.lat,
        longitude: tree.location.lon,
      } } : {}),
    };
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${url}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: SITE_NAME, item: localizedUrl("/", language) },
        { "@type": "ListItem", position: 2, name: "Trees", item: localizedUrl("/catalog", language) },
        { "@type": "ListItem", position: 3, name: treeName, item: url },
      ],
    });
  }

  graph.push(page);
  return { "@context": "https://schema.org", "@graph": graph };
}

function syncMeta(attribute: "name" | "property", key: string, value?: string) {
  const selector = `meta[${attribute}="${key}"]`;
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!value) {
    node?.remove();
    return;
  }
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute(attribute, key);
    document.head.appendChild(node);
  }
  node.content = value;
}

function syncCanonical(url: string) {
  let node = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!node) {
    node = document.createElement("link");
    node.rel = "canonical";
    document.head.appendChild(node);
  }
  node.href = url;
}

function syncAlternates(pathname: string) {
  document.head.querySelectorAll("link[data-seo-alternate]").forEach((node) => node.remove());
  for (const language of SEO_LANGUAGES) {
    const link = document.createElement("link");
    link.rel = "alternate";
    link.hreflang = language;
    link.href = localizedUrl(pathname, language);
    link.dataset.seoAlternate = "true";
    document.head.appendChild(link);
  }
  const fallback = document.createElement("link");
  fallback.rel = "alternate";
  fallback.hreflang = "x-default";
  fallback.href = localizedUrl(pathname, "en");
  fallback.dataset.seoAlternate = "true";
  document.head.appendChild(fallback);
}

export function applyDocumentSeo(metadata: DocumentSeo) {
  const url = localizedUrl(metadata.pathname, metadata.language);
  document.title = metadata.title;
  document.documentElement.lang = metadata.language;
  syncCanonical(url);
  syncAlternates(metadata.pathname);

  syncMeta("name", "description", metadata.description);
  syncMeta("name", "robots", metadata.noindex ? "noindex,follow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1");
  syncMeta("property", "og:type", metadata.type ?? "website");
  syncMeta("property", "og:site_name", SITE_NAME);
  syncMeta("property", "og:title", metadata.title);
  syncMeta("property", "og:description", metadata.description);
  syncMeta("property", "og:url", url);
  syncMeta("property", "og:locale", OPEN_GRAPH_LOCALES[metadata.language]);
  syncMeta("property", "og:image", metadata.image.url);
  syncMeta("property", "og:image:secure_url", metadata.image.url.startsWith("https://") ? metadata.image.url : undefined);
  syncMeta("property", "og:image:type", metadata.image.type);
  syncMeta("property", "og:image:width", metadata.image.width?.toString());
  syncMeta("property", "og:image:height", metadata.image.height?.toString());
  syncMeta("property", "og:image:alt", metadata.imageAlt);

  document.head.querySelectorAll("meta[data-seo-og-alternate]").forEach((node) => node.remove());
  for (const language of SEO_LANGUAGES.filter((candidate) => candidate !== metadata.language)) {
    const meta = document.createElement("meta");
    meta.setAttribute("property", "og:locale:alternate");
    meta.content = OPEN_GRAPH_LOCALES[language];
    meta.dataset.seoOgAlternate = "true";
    document.head.appendChild(meta);
  }

  syncMeta("name", "twitter:card", "summary_large_image");
  syncMeta("name", "twitter:title", metadata.title);
  syncMeta("name", "twitter:description", metadata.description);
  syncMeta("name", "twitter:image", metadata.image.url);
  syncMeta("name", "twitter:image:alt", metadata.imageAlt);

  let structured = document.head.querySelector<HTMLScriptElement>("#structured-data");
  if (!structured) {
    structured = document.createElement("script");
    structured.id = "structured-data";
    structured.type = "application/ld+json";
    document.head.appendChild(structured);
  }
  structured.textContent = JSON.stringify(metadata.structuredData).replace(/</g, "\\u003c");
}

export function localizedCatalogueItems(trees: Tree[], nameOf: (tree: Tree) => string): CatalogueItem[] {
  return trees.map((tree) => ({ name: nameOf(tree), path: `/tree/${tree.id}` }));
}
